import { useState } from 'react';
import type { PanelCreditContractDto } from '@icebreaker/shared';
import { Button, Skeleton, Tag } from '@icebreaker/ui';
import { numericDateOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { fetchCreditOverview } from '../store/credits.slice';
import { ContractCreateDialog, ContractTerminateDialog } from './contract-dialogs';
import styles from './organizations.module.css';

/**
 * KURUMSAL SÖZLEŞME (P5 bölüm 4): başlangıç, bitiş, referans, oluşturan.
 * Geçmiş sözleşmeler soluk satırlardır. Etkin sözleşme varken "Sözleşme
 * tanımla" kapalıdır ve nedeni yanında yazar (referans kuralı); uzatma, etkin
 * sözleşme sonlandırıldıktan ya da bittikten sonra yeni satırdır.
 */
export function ContractCard({
  organizationId,
  organizationName,
}: {
  organizationId: string;
  organizationName: string;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const overview = useAppSelector((root: RootState) => root.panelCredits.overview);
  const [dialog, setDialog] = useState<'create' | PanelCreditContractDto | null>(null);
  const data = overview.organizationId === organizationId ? overview.data : null;
  const contracts = data ? sortContracts(data.contracts) : [];
  const active = contracts.find((contract) => contract.active) ?? null;
  const dateOptions = numericDateOptions(t.locale);

  return (
    <div className={styles.card}>
      <div className={styles.cardTitleRow}>
        <h2 className={styles.cardTitle}>{t.t('admin.credit.contract.title')}</h2>
        {active ? <Tag variant="accent">{t.t('admin.credit.contract.active')}</Tag> : null}
      </div>
      {!data && overview.phase === 'failed' ? (
        <div className={styles.sectionState} role="alert">
          <p className={styles.sectionError}>{t.t('admin.credit.contract.loadError')}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => dispatch(fetchCreditOverview.request({ organizationId }))}
          >
            {t.t('admin.orgs.retry')}
          </Button>
        </div>
      ) : !data ? (
        <div className={styles.sectionLoading}>
          <Skeleton label={t.t('admin.credit.loading')} lines={2} />
        </div>
      ) : (
        <>
          {contracts.length === 0 ? (
            <p className={styles.contractNone}>{t.t('admin.credit.contract.none')}</p>
          ) : (
            <div className={styles.contractRows}>
              {contracts.map((contract) => (
                <div
                  key={contract.id}
                  className={[styles.contractRow, contract.active ? '' : styles.contractPast]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span>
                    <span className={styles.contractLabel}>
                      {t.t('admin.credit.contract.start')}
                    </span>
                    <span className={styles.contractValue}>
                      {t.formatDate(contract.startsAt, dateOptions)}
                    </span>
                  </span>
                  <span>
                    <span className={styles.contractLabel}>{t.t('admin.credit.contract.end')}</span>
                    <span className={styles.contractValue}>
                      {t.formatDate(contract.endsAt, dateOptions)}
                    </span>
                  </span>
                  <span>
                    <span className={styles.contractLabel}>
                      {t.t('admin.credit.contract.reference')}
                    </span>
                    <span className={styles.contractRef}>{contract.reference ?? '—'}</span>
                  </span>
                  <span>
                    <span className={styles.contractLabel}>
                      {t.t('admin.credit.contract.createdBy')}
                    </span>
                    {contract.createdBy.displayName ?? t.t('admin.credit.ledger.system')}
                    {contract.terminatedAt ? (
                      <span className={styles.contractLabel}>
                        {t.t('admin.credit.contract.terminatedAt', {
                          date: t.formatDate(contract.terminatedAt, dateOptions),
                        })}
                      </span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className={styles.cardActions}>
            <Button
              variant="secondary"
              size="sm"
              softDisabled={active !== null}
              disabledReasonId="contract-blocked-note"
              onClick={() => setDialog('create')}
            >
              {t.t('admin.credit.contract.define')}
            </Button>
            {active ? (
              <>
                <Button variant="dangerOutline" size="sm" onClick={() => setDialog(active)}>
                  {t.t('admin.credit.contract.terminate')}
                </Button>
                <span id="contract-blocked-note" className={styles.actionNote}>
                  {t.t('admin.credit.contract.blockedNote')}
                </span>
              </>
            ) : null}
          </div>
        </>
      )}
      {dialog === 'create' ? (
        <ContractCreateDialog
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setDialog(null)}
        />
      ) : dialog ? (
        <ContractTerminateDialog
          organizationId={organizationId}
          contract={dialog}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

/** Etkin sözleşme önce, geçmişler başlangıca göre yeniden eskiye. */
function sortContracts(contracts: readonly PanelCreditContractDto[]): PanelCreditContractDto[] {
  return [...contracts].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return b.startsAt.localeCompare(a.startsAt);
  });
}
